import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '../../components/tulote/Navigation';
import {
  MapPin,
  Building2,
  Users,
  TrendingUp,
  CheckCircle,
  Sparkles,
  Target,
  Shield,
  Zap
} from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import { getUrlWithCountry } from '../../utils/countryUtils';
import brandConfig from '../../config/brandConfig';
import { Button, Container, Section, FeatureCard } from '../../components/ui';

function WelcomePage() {
  const { country } = useCountry();

  const getPath = (path) => {
    return getUrlWithCountry(path, country);
  };

  const features = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Visualización Interactiva',
      description: 'Explora proyectos con mapas interactivos de última generación y visualización detallada de cada lote disponible en tiempo real.'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Gestión Profesional',
      description: 'Herramientas completas e intuitivas para gestionar proyectos, lotes, clientes y generar cotizaciones profesionales.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Análisis y Reportes',
      description: 'Obtén insights valiosos con análisis detallados de ventas, métricas de rendimiento y reportes personalizables.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Seguridad Garantizada',
      description: 'Tus datos están protegidos con los más altos estándares de seguridad y encriptación de nivel empresarial.'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Rendimiento Óptimo',
      description: 'Plataforma optimizada para velocidad y eficiencia, garantizando una experiencia fluida en cualquier dispositivo.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Soporte Dedicado',
      description: 'Equipo de soporte experto disponible para ayudarte en cada paso de tu viaje inmobiliario.'
    }
  ];

  const benefits = [
    'Acceso desde cualquier dispositivo',
    'Actualizaciones en tiempo real',
    'Sin límite de proyectos',
    'Exportación de datos',
    'Integración con WhatsApp',
    'Reportes personalizados'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
      <Navigation />

      {/* Hero Section - Mejorado */}
      <Section variant="gradient" className="relative overflow-hidden pt-32 pb-20">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/4 w-[800px] h-[800px] bg-orange-500/10 dark:bg-orange-500/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/4 w-[600px] h-[600px] bg-blue-light-500/10 dark:bg-blue-light-500/5 rounded-full blur-3xl" />
        </div>

        <Container className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full mb-8 border border-orange-200 dark:border-orange-700"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">Plataforma Inmobiliaria Profesional</span>
            </motion.div>

            {/* Main heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Transforma tu
              <span className="block mt-2 bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
                Negocio Inmobiliario
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed"
            >
              Descubre, gestiona y vende proyectos inmobiliarios con la plataforma más completa y moderna del mercado.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link to={getPath('/proyectos')}>
                <Button size="lg" icon={<MapPin className="w-5 h-5" />}>
                  Explorar Proyectos
                </Button>
              </Link>
              <Link to={getPath('/como-agregar-proyectos')}>
                <Button variant="secondary" size="lg" icon={<Building2 className="w-5 h-5" />}>
                  Agregar Mi Proyecto
                </Button>
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-12 flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600 dark:text-gray-400"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <span>Gratis para comenzar</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <span>Sin tarjeta de crédito</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <span>Configuración en minutos</span>
              </div>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Features Section - Mejorado */}
      <Section variant="default">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Todo lo que necesitas en un solo lugar
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Potencia tu negocio con herramientas diseñadas para profesionales inmobiliarios
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={index * 0.1}
              />
            ))}
          </div>
        </Container>
      </Section>

      {/* Benefits Section - Nuevo */}
      <Section variant="gray">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full mb-6">
                <Target className="w-4 h-4" />
                <span className="text-sm font-semibold">Beneficios</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Diseñado para tu éxito
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                LotesPro.com te brinda todas las herramientas que necesitas para gestionar tu negocio inmobiliario de manera eficiente y profesional.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/10 bg-[size:20px_20px]" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <Building2 className="w-24 h-24 mx-auto mb-4 opacity-90" />
                    <p className="text-2xl font-bold">Vista previa del mapa</p>
                    <p className="text-orange-100 mt-2">Visualización interactiva</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* CTA Section - Mejorado */}
      <Section variant="primary" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5 bg-[size:40px_40px]" />
        <Container className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              ¿Listo para transformar tu negocio?
            </h2>
            <p className="text-xl text-orange-50 mb-10 leading-relaxed">
              Únete a cientos de profesionales que ya están usando LotesPro.com para gestionar sus proyectos inmobiliarios de manera más eficiente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={getPath('/como-agregar-proyectos')}>
                <Button
                  variant="secondary"
                  size="lg"
                  icon={<Building2 className="w-5 h-5" />}
                  className="bg-white hover:bg-gray-100 text-orange-600"
                >
                  Comenzar Ahora
                </Button>
              </Link>
              <a href={`${brandConfig.clientHomeUrl}#contact`}>
                <Button
                  variant="outline"
                  size="lg"
                  icon={<Users className="w-5 h-5" />}
                  className="text-white border-white hover:bg-white/10"
                >
                  Contáctanos
                </Button>
              </a>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}

export default WelcomePage;
